/**
 * User Management Redux Slice
 * - Handles pet parents and veterinarians data
 * - Manages pagination for user listings
 * - Supports CRUD operations for users
 * - Separate state for pet parents and veterinarians
 */

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchUsersAPI,
  fetchUserByIdAPI,
  deleteUserAPI,
  updateUserAPI,
} from "../../services/userAPI";

// ✅ Fetch all users with pagination
export const fetchUsers = createAsyncThunk(
  "users/fetchUsers",
  async ({ userType, page = 1 }) => {
    const response = await fetchUsersAPI(userType, page);
    return { userType, data: response.data.data, pagination: response.data.pagination };
  }
);

// ✅ Fetch single user
export const fetchUserById = createAsyncThunk(
  "users/fetchUserById",
  async ({ userType, id }) => {
    const response = await fetchUserByIdAPI(userType, id);
    console.log('Fetched user data:', response.data.data);
    
    return response.data.data;
  }
);

// ✅ Delete user
export const deleteUser = createAsyncThunk(
  "users/deleteUser",
  async ({ userType, id }) => {
    console.log(userType,id);
    
    await deleteUserAPI(userType, id);
    return { userType, id };
  }
);

// ✅ Update user
export const updateUser = createAsyncThunk(
  "users/updateUser",
  async ({ userType, id, data }, { rejectWithValue }) => {
    try {
      const response = await updateUserAPI(userType, id, data);
      return response.data.data;
    } catch (error) {
      // Return the original error so it can be accessed in the component
      return rejectWithValue(error);
    }
  }
);

const initialState = {
  petParents: [],
  veterinarians: [],
  profile: null,
  listStatus: "idle", // ✅ clearer: for fetchUsers
  profileStatus: "idle", // ✅ clearer: for fetchUserById
  error: null,
  // Pagination state
  pagination: {
    petParents: {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      itemsPerPage: 10
    },
    veterinarians: {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      itemsPerPage: 10
    }
  }
};

const userSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    clearProfile: (state) => {
      state.profile = null;
      state.profileStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      // ✅ Fetch all users
      .addCase(fetchUsers.pending, (state) => {
        state.listStatus = "loading";
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.listStatus = "succeeded";
        const { userType, data, pagination } = action.payload;
        if (userType === "pet-parents") {
          state.petParents = (data.users || []).map(user => ({
            ...user,
            status: user.active ? 'Active' : 'Inactive'
          }));
          // Update pagination for pet parents
          if (pagination) {
            state.pagination.petParents = {
              currentPage: pagination.current_page || 1,
              totalPages: pagination.total_pages || 1,
              totalItems: pagination.total_count || 0,
              itemsPerPage: pagination.per_page || 10
            };
          }
        } else {
          state.veterinarians = (data.users || []).map(user => ({
            ...user,
            kycStatus: user.vendor?.kyc_status || 'NA',
            specialty: user.vendor?.specialization || 'NA'
          }));
          // Update pagination for veterinarians
          if (pagination) {
            state.pagination.veterinarians = {
              currentPage: pagination.current_page || 1,
              totalPages: pagination.total_pages || 1,
              totalItems: pagination.total_count || 0,
              itemsPerPage: pagination.per_page || 10
            };
          }
        }
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.listStatus = "failed";
        state.error = action.error.message;
      })

      // ✅ Fetch single user
      .addCase(fetchUserById.pending, (state) => {
        state.profileStatus = "loading";
        state.profile = null;
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.profileStatus = "succeeded";
        state.profile = action.payload;
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.profileStatus = "failed";
        state.error = action.error.message;
      })

      // ✅ Delete user
      .addCase(deleteUser.fulfilled, (state, action) => {
        const { userType, id } = action.payload;
        if (userType === "pet-parents") {
          state.petParents = state.petParents.filter((u) => u.uuid !== id);
        } else {
          state.veterinarians = state.veterinarians.filter((u) => u.uuid !== id);
        }
        if (state.profile?.uuid === id) {
          state.profile = null;
          state.profileStatus = "idle";
        }
      })

      // ✅ Update user
      .addCase(updateUser.fulfilled, (state, action) => {
        state.profile = action.payload; // refresh profile
        const listKey = action.payload.type === "pet-parents" ? "petParents" : "veterinarians";
        state[listKey] = state[listKey].map((u) =>
          u.user_code === action.payload.user_code ? action.payload : u
        );
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.error = action.error.message;
      });
  },
});

export const { clearProfile } = userSlice.actions;
export default userSlice.reducer;
